import React, { useState } from 'react';
import { X, Plus, Trash2, Save, Send, Upload, Image as ImageIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';

export default function InvoiceForm({ invoice = null, onClose, onSubmit }) {
  const isEditMode = !!invoice;
  const [logoPreview, setLogoPreview] = useState(invoice?.logo || null);
  
  const [formData, setFormData] = useState({
    // Invoice Details
    invoiceNumber: invoice?.invoiceNumber || '',
    invoiceDate: invoice?.invoiceDate || new Date().toISOString().split('T')[0],
    dueDate: invoice?.dueDate || '',
    purchaseOrder: invoice?.purchaseOrder || '',
    
    // Seller Details
    logo: invoice?.logo || null,
    sellerName: invoice?.sellerName || '',
    sellerAddress: invoice?.sellerAddress || '',
    sellerCity: invoice?.sellerCity || '',
    sellerState: invoice?.sellerState || '',
    sellerZip: invoice?.sellerZip || '',
    sellerGSTIN: invoice?.sellerGSTIN || '',
    sellerPAN: invoice?.sellerPAN || '',
    
    // Billed To (Buyer)
    buyerName: invoice?.buyerName || '',
    buyerAddress: invoice?.buyerAddress || '',
    buyerCity: invoice?.buyerCity || '',
    buyerState: invoice?.buyerState || '',
    buyerZip: invoice?.buyerZip || '',
    buyerGSTIN: invoice?.buyerGSTIN || '',
    buyerPAN: invoice?.buyerPAN || '',
    
    // Shipped To (Consignee) - can be same as buyer
    shipToSameAsBuyer: invoice?.shipToSameAsBuyer || true,
    consigneeName: invoice?.consigneeName || '',
    consigneeAddress: invoice?.consigneeAddress || '',
    consigneeCity: invoice?.consigneeCity || '',
    consigneeState: invoice?.consigneeState || '',
    consigneeZip: invoice?.consigneeZip || '',
    
    // Line Items
    items: invoice?.items || [
      {
        description: '',
        hsnCode: '',
        quantity: 1,
        unit: 'Nos',
        rate: 0,
        discount: 0,
        taxable: true
      }
    ],
    
    // Tax Settings
    cgstRate: invoice?.cgstRate || 9,
    sgstRate: invoice?.sgstRate || 9,
    igstRate: invoice?.igstRate || 0,
    
    // Bank Details
    bankName: invoice?.bankName || '',
    accountNumber: invoice?.accountNumber || '',
    ifscCode: invoice?.ifscCode || '',
    branch: invoice?.branch || '',
    
    // Additional
    notes: invoice?.notes || '',
    terms: invoice?.terms || 'Payment due within 15 days.\nLate payments may incur additional charges.',
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('Logo file size must be less than 2MB');
        return;
      }
      
      // Check file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        alert('Only PNG, JPG, JPEG, and SVG images are allowed');
        return;
      }
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setFormData(prev => ({
          ...prev,
          logo: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setFormData(prev => ({
      ...prev,
      logo: null
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        description: '',
        hsnCode: '',
        quantity: 1,
        unit: 'Nos',
        rate: 0,
        discount: 0,
        taxable: true
      }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, items: newItems }));
    }
  };

  const calculateItemTotal = (item) => {
    const subtotal = item.quantity * item.rate;
    const discountAmount = (subtotal * item.discount) / 100;
    return subtotal - discountAmount;
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
    const taxableAmount = formData.items
      .filter(item => item.taxable)
      .reduce((sum, item) => sum + calculateItemTotal(item), 0);
    
    const cgst = (taxableAmount * formData.cgstRate) / 100;
    const sgst = (taxableAmount * formData.sgstRate) / 100;
    const igst = (taxableAmount * formData.igstRate) / 100;
    const totalTax = cgst + sgst + igst;
    const total = subtotal + totalTax;
    
    return { subtotal, cgst, sgst, igst, totalTax, total };
  };

  const totals = calculateTotals();

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ ...formData, ...totals });
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center justify-between">
            <span>{isEditMode ? 'Edit Invoice' : 'Create New Invoice'}</span>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Invoice Details */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-lg mb-4">Invoice Details</h3>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice Number *
                </label>
                <Input
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleInputChange}
                  placeholder="INV-001"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice Date *
                </label>
                <Input
                  type="date"
                  name="invoiceDate"
                  value={formData.invoiceDate}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date *
                </label>
                <Input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purchase Order
                </label>
                <Input
                  name="purchaseOrder"
                  value={formData.purchaseOrder}
                  onChange={handleInputChange}
                  placeholder="PO-123"
                />
              </div>
            </div>
          </div>

          {/* Seller & Buyer Details in Grid */}
          <div className="grid grid-cols-2 gap-6">
            {/* Seller Details */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-lg mb-4">Seller Details (Your Information)</h3>
              <div className="space-y-3">
                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Logo
                  </label>
                  {logoPreview ? (
                    <div className="flex items-start space-x-4">
                      <div className="relative">
                        <img 
                          src={logoPreview} 
                          alt="Business Logo" 
                          className="h-24 w-24 object-contain border-2 border-gray-300 rounded-lg bg-white p-2"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveLogo}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-600 mb-2">Logo uploaded successfully</p>
                        <label className="cursor-pointer">
                          <span className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                            <Upload className="w-4 h-4 mr-2" />
                            Change Logo
                          </span>
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                            onChange={handleLogoUpload}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors">
                        <ImageIcon className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm font-medium text-gray-700 mb-1">
                          Click to upload logo
                        </p>
                        <p className="text-xs text-gray-500">
                          PNG, JPG, JPEG, or SVG (max 2MB)
                        </p>
                      </div>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Name *
                  </label>
                  <Input
                    name="sellerName"
                    value={formData.sellerName}
                    onChange={handleInputChange}
                    placeholder="Your Company Name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address *
                  </label>
                  <Textarea
                    name="sellerAddress"
                    value={formData.sellerAddress}
                    onChange={handleInputChange}
                    placeholder="Street address"
                    rows={2}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                    <Input
                      name="sellerCity"
                      value={formData.sellerCity}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                    <Input
                      name="sellerState"
                      value={formData.sellerState}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                    <Input
                      name="sellerZip"
                      value={formData.sellerZip}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
                    <Input
                      name="sellerGSTIN"
                      value={formData.sellerGSTIN}
                      onChange={handleInputChange}
                      placeholder="22AAAAA0000A1Z5"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PAN</label>
                  <Input
                    name="sellerPAN"
                    value={formData.sellerPAN}
                    onChange={handleInputChange}
                    placeholder="AAAAA0000A"
                  />
                </div>
              </div>
            </div>

            {/* Buyer Details */}
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="font-semibold text-lg mb-4">Billed To (Buyer Details)</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client Name *
                  </label>
                  <Input
                    name="buyerName"
                    value={formData.buyerName}
                    onChange={handleInputChange}
                    placeholder="Client Company Name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address *
                  </label>
                  <Textarea
                    name="buyerAddress"
                    value={formData.buyerAddress}
                    onChange={handleInputChange}
                    placeholder="Street address"
                    rows={2}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                    <Input
                      name="buyerCity"
                      value={formData.buyerCity}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                    <Input
                      name="buyerState"
                      value={formData.buyerState}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                    <Input
                      name="buyerZip"
                      value={formData.buyerZip}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
                    <Input
                      name="buyerGSTIN"
                      value={formData.buyerGSTIN}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PAN</label>
                  <Input
                    name="buyerPAN"
                    value={formData.buyerPAN}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Shipped To (optional) */}
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Shipped To (Consignee)</h3>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="shipToSameAsBuyer"
                  checked={formData.shipToSameAsBuyer}
                  onChange={handleInputChange}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium text-gray-700">Same as Billed To</span>
              </label>
            </div>
            {!formData.shipToSameAsBuyer && (
              <div className="grid grid-cols-3 gap-4">
                <Input
                  name="consigneeName"
                  value={formData.consigneeName}
                  onChange={handleInputChange}
                  placeholder="Consignee Name"
                />
                <Input
                  name="consigneeAddress"
                  value={formData.consigneeAddress}
                  onChange={handleInputChange}
                  placeholder="Address"
                />
                <Input
                  name="consigneeCity"
                  value={formData.consigneeCity}
                  onChange={handleInputChange}
                  placeholder="City"
                />
              </div>
            )}
          </div>

          {/* Line Items */}
          <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Line Items</h3>
              <Button type="button" size="sm" onClick={addItem} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left p-2 w-1/3">Description *</th>
                    <th className="text-left p-2 w-24">HSN Code</th>
                    <th className="text-center p-2 w-20">Qty *</th>
                    <th className="text-left p-2 w-20">Unit</th>
                    <th className="text-right p-2 w-24">Rate *</th>
                    <th className="text-right p-2 w-20">Disc %</th>
                    <th className="text-center p-2 w-16">Tax</th>
                    <th className="text-right p-2 w-24">Amount</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-2">
                        <Textarea
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          placeholder="Service/Product description"
                          rows={2}
                          required
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.hsnCode}
                          onChange={(e) => handleItemChange(index, 'hsnCode', e.target.value)}
                          placeholder="998314"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          required
                        />
                      </td>
                      <td className="p-2">
                        <select
                          value={item.unit}
                          onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                          className="w-full border rounded p-1.5"
                        >
                          <option value="Nos">Nos</option>
                          <option value="Hours">Hours</option>
                          <option value="Days">Days</option>
                          <option value="Pcs">Pcs</option>
                        </select>
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          value={item.rate}
                          onChange={(e) => handleItemChange(index, 'rate', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          required
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          value={item.discount}
                          onChange={(e) => handleItemChange(index, 'discount', parseFloat(e.target.value) || 0)}
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </td>
                      <td className="p-2 text-center">
                        <input
                          type="checkbox"
                          checked={item.taxable}
                          onChange={(e) => handleItemChange(index, 'taxable', e.target.checked)}
                          className="w-4 h-4"
                        />
                      </td>
                      <td className="p-2 text-right font-medium">
                        ${calculateItemTotal(item).toFixed(2)}
                      </td>
                      <td className="p-2">
                        {formData.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="mt-4 flex justify-end">
              <div className="w-80 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">${totals.subtotal.toFixed(2)}</span>
                </div>
                {totals.cgst > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">CGST ({formData.cgstRate}%):</span>
                    <span className="font-medium">${totals.cgst.toFixed(2)}</span>
                  </div>
                )}
                {totals.sgst > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">SGST ({formData.sgstRate}%):</span>
                    <span className="font-medium">${totals.sgst.toFixed(2)}</span>
                  </div>
                )}
                {totals.igst > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">IGST ({formData.igstRate}%):</span>
                    <span className="font-medium">${totals.igst.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Total:</span>
                  <span className="text-green-600">${totals.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tax Rates */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-lg mb-4">Tax Configuration</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CGST %</label>
                <Input
                  type="number"
                  name="cgstRate"
                  value={formData.cgstRate}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SGST %</label>
                <Input
                  type="number"
                  name="sgstRate"
                  value={formData.sgstRate}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IGST %</label>
                <Input
                  type="number"
                  name="igstRate"
                  value={formData.igstRate}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="font-semibold text-lg mb-4">Bank Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                <Input
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleInputChange}
                  placeholder="e.g., HDFC Bank"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                <Input
                  name="accountNumber"
                  value={formData.accountNumber}
                  onChange={handleInputChange}
                  placeholder="1234567890"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IFSC Code</label>
                <Input
                  name="ifscCode"
                  value={formData.ifscCode}
                  onChange={handleInputChange}
                  placeholder="HDFC0001234"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                <Input
                  name="branch"
                  value={formData.branch}
                  onChange={handleInputChange}
                  placeholder="Branch name"
                />
              </div>
            </div>
          </div>

          {/* Notes and Terms */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <Textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Additional notes or comments"
                rows={4}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
              <Textarea
                name="terms"
                value={formData.terms}
                onChange={handleInputChange}
                placeholder="Payment terms and conditions"
                rows={4}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="bg-green-600 hover:bg-green-700">
              <Save className="w-4 h-4 mr-2" />
              {isEditMode ? 'Update Invoice' : 'Create Invoice'}
            </Button>
            <Button type="button" className="bg-blue-600 hover:bg-blue-700">
              <Send className="w-4 h-4 mr-2" />
              Save & Send
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
